import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

/**
 * InfoTooltip - En genbrugelig komponent til at vise forklaringer på grafer
 * 
 * Brug:
 * <InfoTooltip 
 *   title="KPT (Kunder pr. time)"
 *   description="KPT beregnes som antal opmålinger divideret med antal arbejdstimer."
 *   formula="KPT = Antal opmålinger ÷ Arbejdstimer"
 *   details={[
 *     "Kun konsulenter og konsulent/montører indgår",
 *     "Konsulent/montør tælles med 60% af deres tid",
 *     "Fravær (sygdom, ferie, barsel) fratrækkes"
 *   ]}
 * />
 */
const InfoTooltip = ({ title, description, formula, details, dataSource }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Info Icon Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="p-1 rounded-full hover:bg-white/10 transition-colors group"
        title="Vis forklaring"
      >
        <Info className="w-4 h-4 text-white/40 group-hover:text-white/70" />
      </button>

      {/* Modal Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
          onClick={() => setIsOpen(false)}
        >
          {/* Modal Content */}
          <div 
            className="bg-[#242424] border border-white/10 rounded-xl max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-white/10">
              <h3 className="text-lg font-semibold text-white">{title}</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 rounded-full hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/60" />
              </button>
            </div>

            {/* Body */}
            <div className="p-4 space-y-4">
              {/* Description */}
              {description && (
                <p className="text-sm text-white/80">{description}</p>
              )}

              {/* Formula */}
              {formula && (
                <div className="bg-white/5 rounded-lg p-3">
                  <p className="text-xs text-white/50 mb-1">Formel</p>
                  <p className="text-sm font-mono text-[#1E6F7A]">{formula}</p>
                </div>
              )}

              {/* Details List */}
              {details && details.length > 0 && (
                <div>
                  <p className="text-xs text-white/50 mb-2">Detaljer</p>
                  <ul className="space-y-1">
                    {details.map((detail, index) => (
                      <li key={index} className="text-sm text-white/70 flex items-start gap-2">
                        <span className="text-[#1E6F7A] mt-1">•</span>
                        <span>{detail}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Data Source */}
              {dataSource && (
                <div className="pt-2 border-t border-white/10">
                  <p className="text-xs text-white/40">
                    Datakilde: {dataSource}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default InfoTooltip;
